import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ClipboardList, ChevronRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function getCategoryDisplayName(cat) {
  if (!cat) return 'Kategori';
  const parts = [cat.kategori, cat.teknik].filter(Boolean);
  return parts.length ? parts.join(' - ') : `Kategori ${cat.id || ''}`;
}

const RegistrationCategoryPicker = ({ open, onOpenChange, onSelect }) => {
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;

    let isMounted = true;
    setLoading(true);
    setError('');

    const fetchData = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/tournaments`, {
          headers: { Authorization: token ? `Bearer ${token}` : undefined },
        });
        if (isMounted) {
          setTournaments(Array.isArray(response.data) ? response.data : []);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.response?.data?.detail || 'Gagal memuat turnamen');
          setTournaments([]);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, [open]);

  const handleSelect = (tournamentId, categoryId) => {
    const url = `/registrations?tournamentId=${encodeURIComponent(String(tournamentId))}&categoryId=${encodeURIComponent(
      String(categoryId)
    )}`;
    onSelect?.(url);
    onOpenChange?.(false);
  };

  const items = tournaments.flatMap((t) => {
    const cats = t.categories || [];
    if (cats.length === 0 && (t.kategori || t.teknik)) {
      const legacyCat = {
        id: `legacy-${t.id}`,
        kategori: t.kategori || 'Individu',
        teknik: t.teknik || 'Freestyle',
        sistem: t.sistem || 'Gugur',
      };
      return [{ tournament: t, category: legacyCat }];
    }
    return cats.map((c) => ({ tournament: t, category: c }));
  });

  const content = (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-1.5rem)] sm:max-w-md max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Pilih Kategori Registrasi
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0 -mx-1 px-1">
          {loading && (
            <div className="py-8 text-center text-slate-500 text-sm">Memuat...</div>
          )}
          {error && (
            <div className="py-4 text-center text-red-600 text-sm">{error}</div>
          )}
          {!loading && !error && items.length === 0 && (
            <div className="py-8 text-center text-slate-500 text-sm">Belum ada kategori turnamen</div>
          )}
          {!loading && !error && items.length > 0 && (
            <ul className="space-y-1">
              {items.map(({ tournament, category }) => (
                <li key={`${tournament.id}-${category.id}`}>
                  <button
                    type="button"
                    onClick={() => handleSelect(tournament.id, category.id)}
                    className="w-full flex items-center justify-between gap-3 px-4 py-3.5 min-h-[52px] rounded-xl text-left border border-slate-200 hover:border-sky-300 hover:bg-sky-50/50 transition-colors touch-manipulation"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-800 truncate">
                        {tournament.nama || `Turnamen ${tournament.id}`}
                      </div>
                      <div className="text-sm text-slate-500 truncate mt-0.5">
                        {getCategoryDisplayName(category)}
                      </div>
                    </div>
                    <ChevronRight size={20} className="shrink-0 text-slate-400" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );

  return createPortal(content, document.body);
};

export default RegistrationCategoryPicker;
